import React from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { DonationTier } from "./types";
import { donationTiers } from "./data/mockData";

interface DonateSectionProps {
  donationAmount: number;
  setDonationAmount: (val: number) => void;
  donorName: string;
  setDonorName: (val: string) => void;
  donorMessage: string;
  setDonorMessage: (val: string) => void;
  isDonating: boolean;
  onDonate: (e: React.FormEvent) => Promise<void> | void;
}

const TierCard: React.FC<{
  tier: DonationTier;
  selected: boolean;
  onSelect: () => void;
}> = ({ tier, selected, onSelect }) => (
  <Button
    onClick={onSelect}
    sx={{
      display: "block",
      textAlign: "left",
      p: 3,
      borderRadius: 6,
      bgcolor: tier.featured ? "#6a4331" : "#5c3a2a",
      color: "white",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.2s",
      border: selected ? "3px solid #fb923c" : "3px solid transparent",
      outline: selected ? "3px solid rgba(251,146,60,0.3)" : "none",
      outlineOffset: 3,
      "&:hover": {
        transform: "scale(1.03)",
        bgcolor: tier.featured ? "#5c3a2a" : "#4a2f22",
      },
    }}
  >
    <WbSunnyIcon
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        fontSize: 60,
        opacity: 0.15,
      }}
    />
    <Typography
      variant="h4"
      sx={{
        fontSize: "1.8rem",
        fontWeight: 900,
        mb: 0.5,
      }}
    >
      ${tier.amt}
    </Typography>
    <Typography variant="subtitle2" sx={{ color: "#fed7aa", mb: 1.5 }}>
      {tier.label}
    </Typography>
    <Typography variant="body2" sx={{ color: "#f1f5f9" }}>
      {tier.benefit}
    </Typography>
  </Button>
);

const Donate: React.FC<DonateSectionProps> = ({
  donationAmount,
  setDonationAmount,
  donorName,
  setDonorName,
  donorMessage,
  setDonorMessage,
  isDonating,
  onDonate,
}) => {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await onDonate(e);

    // Clear form after submit
    setDonationAmount(0);
    setDonorName("");
    setDonorMessage("");
  };

  return (
    <Box
      component="section"
      id="donate-section"
      sx={{
        py: 12,
        bgcolor: "#7B4F3A",
        color: "white",
        position: "relative",
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto", px: 2 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography variant="h2" sx={{ fontSize: "2.5rem", mb: 2 }}>
            Support the Mission
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.75)",
              fontSize: "1.1rem",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Select a giving tier to light up a sanctuary. Every dollar provides
            over 20 years of renewable power.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
            gap: 4,
            alignItems: "start",
          }}
        >
          {/* Tiers */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {donationTiers.map((tier) => (
              <TierCard
                key={tier.amt}
                tier={tier}
                selected={donationAmount === tier.amt}
                onSelect={() => setDonationAmount(tier.amt)}
              />
            ))}
          </Box>

          {/* Form */}
          <Card
            sx={{
              borderRadius: 6,
              bgcolor: "#f5f5f4",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Contribution Amount
                  </Typography>
                  <TextField
                    type="number"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(Number(e.target.value))}
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Your Name (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Words of Blessing
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={donorMessage}
                    onChange={(e) => setDonorMessage(e.target.value)}
                  />
                </Box>

                <Button
                  type="submit"
                  disabled={isDonating}
                  fullWidth
                  sx={{
                    py: 2,
                    fontSize: "1.2rem",
                    fontWeight: 900,
                    borderRadius: 4,
                    bgcolor: "#7B4F3A",
                    color: "white",
                    "&:hover": {
                      bgcolor: "#6a4331",
                    },
                  }}
                >
                  {isDonating ? "Processing..." : "Confirm Blessing"}
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    pt: 1,
                  }}
                >
                  <VerifiedUserIcon
                    sx={{ fontSize: 12, color: "text.disabled" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.disabled",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      fontSize: 10,
                    }}
                  >
                    Secure Partner Payment
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Donate;
